import { useEffect, useState } from 'react';

/**
 * Qual seção está sendo lida agora — o destaque do índice lateral.
 *
 * `IntersectionObserver` com `rootMargin` recortando o topo (a topbar é fixa) e
 * a metade de baixo: sem esse recorte, a seção "ativa" seria sempre a última
 * visível na tela, e o índice ficaria adiantado em relação ao que a pessoa
 * está preenchendo.
 *
 * Se o navegador não tiver o observer (jsdom nos testes), o hook devolve a
 * primeira seção e não quebra nada — o índice continua clicável, só não
 * acompanha a rolagem.
 */
export function useSectionSpy(sectionIds: readonly string[]): string | null {
  const [activeId, setActiveId] = useState<string | null>(sectionIds[0] ?? null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter((element): element is HTMLElement => element !== null);
    if (elements.length === 0) return;

    const visible = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        }
        // A primeira da ordem do documento, e não a que tem maior área: numa
        // seção longa, a de baixo entra em cena antes de a de cima sair.
        const first = sectionIds.find((id) => visible.has(id));
        if (first) setActiveId(first);
      },
      { rootMargin: '-88px 0px -55% 0px', threshold: 0 },
    );

    for (const element of elements) observer.observe(element);
    return () => observer.disconnect();
  }, [sectionIds]);

  return activeId;
}
